import React, { useMemo } from "react";
import {
  Alert,
  Button,
  ConfigProvider,
  DatePicker,
  Form,
  Input,
  Select,
} from "antd";
import ptBR from "antd/es/locale/pt_BR";
import moment from "moment";
import "moment/locale/pt-br";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { EVENT_TYPE_LOJINHA } from "../constants";
import { DATE_PICKER_TIME_CONFIG, DATE_TIME_FORMAT } from "./constants";
import {
  buildLojinhaJsonValue,
  isBeforeStartDateDay,
  isPastDate,
  validateEndDateNotBeforeStartDate,
  validateJsonValue,
  validateNotPastDate,
} from "./utils/eventFormValues";
import { useEditEventForm } from "./hooks/useEditEventForm";
import classes from "./EditEvent.module.scss";

moment.locale("pt-br");

const EditEvent = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const location = useLocation();
  const { event_id } = useParams();
  const isCreateMode = !event_id;

  const event = useMemo(
    () => (isCreateMode ? null : location.state?.event),
    [isCreateMode, location.state],
  );

  const {
    isSaving,
    eventStarted,
    eventTypeOptions,
    handleFinish,
    handleCancel,
  } = useEditEventForm({ form, event, isCreateMode, history });

  const categoryValue = Form.useWatch("category", form);
  const isLojinhaCreation = isCreateMode && categoryValue === EVENT_TYPE_LOJINHA;

  const handleValuesChange = (changedValues) => {
    if (!isCreateMode) {
      return;
    }

    const touchedLojinhaField =
      "layout" in changedValues ||
      "secret_key" in changedValues ||
      "url_iframe" in changedValues ||
      "category" in changedValues;

    if (!touchedLojinhaField) {
      return;
    }

    const values = form.getFieldsValue();

    if (values.category === EVENT_TYPE_LOJINHA) {
      form.setFieldsValue({ json: buildLojinhaJsonValue(values) });
    }
  };

  return (
    <ConfigProvider locale={ptBR}>
      <div className={classes.EditEventPage}>
        <h1 className={classes.Title}>
          {isCreateMode ? "Novo Evento" : "Editar Evento"}
        </h1>

        {!isCreateMode && !event ? (
          <div className={classes.NotFound}>
            <Alert
              type="warning"
              showIcon
              message="Evento não encontrado"
              description="Volte para a listagem e selecione um evento válido para editar."
            />
            <Button className={classes.CancelButton} onClick={handleCancel}>
              Cancelar
            </Button>
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            className={classes.EditEventForm}
            onFinish={handleFinish}
            onValuesChange={handleValuesChange}
          >
            <div className={classes.FormGrid}>
              <Form.Item
                label="Nome do Evento"
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Informe o nome do evento.",
                  },
                ]}
              >
                <Input disabled={eventStarted} />
              </Form.Item>

              <Form.Item
                label="Tipo de evento"
                name="category"
                rules={[
                  {
                    required: true,
                    message: "Selecione o tipo de evento.",
                  },
                ]}
              >
                <Select disabled={!isCreateMode} options={eventTypeOptions} />
              </Form.Item>

              <Form.Item
                label="Data de início"
                name="startDate"
                rules={[
                  {
                    required: true,
                    message: "Informe a data de início.",
                  },
                  ...(isCreateMode
                    ? [
                        {
                          validator: validateNotPastDate,
                        },
                      ]
                    : []),
                ]}
              >
                <DatePicker
                  disabled={eventStarted}
                  disabledDate={isCreateMode ? isPastDate : undefined}
                  format={DATE_TIME_FORMAT}
                  placeholder="00/00/0000 00:00"
                  showTime={DATE_PICKER_TIME_CONFIG}
                />
              </Form.Item>

              <Form.Item
                label="Data de fim"
                name="endDate"
                dependencies={["startDate"]}
                rules={[
                  {
                    required: true,
                    message: "Informe a data de fim.",
                  },
                  ...(isCreateMode
                    ? [
                        {
                          validator: validateNotPastDate,
                        },
                      ]
                    : []),
                  validateEndDateNotBeforeStartDate,
                ]}
              >
                <DatePicker
                  disabled={!isCreateMode}
                  disabledDate={
                    isCreateMode
                      ? (date) =>
                          isPastDate(date) ||
                          isBeforeStartDateDay(
                            date,
                            form.getFieldValue("startDate")
                          )
                      : undefined
                  }
                  format={DATE_TIME_FORMAT}
                  placeholder="00/00/0000 00:00"
                  showTime={DATE_PICKER_TIME_CONFIG}
                />
              </Form.Item>

              {isLojinhaCreation && (
                <>
                  <Form.Item
                    className={classes.LojinhaField}
                    label="Layout"
                    name="layout"
                    rules={[
                      {
                        required: true,
                        message: "Informe o layout.",
                      },
                    ]}
                  >
                    <Input placeholder="Ex: CupNoodle" />
                  </Form.Item>

                  <Form.Item
                    className={classes.LojinhaField}
                    label="Chave secreta"
                    name="secret_key"
                    rules={[
                      {
                        required: true,
                        message: "Informe a chave secreta.",
                      },
                    ]}
                  >
                    <Input placeholder="Ex: AFINANDO312613" />
                  </Form.Item>

                  <Form.Item
                    className={classes.LojinhaField}
                    label="URL do iframe"
                    name="url_iframe"
                    rules={[
                      {
                        required: true,
                        message: "Informe a URL do iframe.",
                      },
                      {
                        type: "url",
                        message: "Informe uma URL válida.",
                      },
                    ]}
                  >
                    <Input placeholder="https://..." />
                  </Form.Item>
                </>
              )}

              <Form.Item
                className={classes.JsonField}
                label="Json"
                name="json"
                rules={[
                  {
                    validator: validateJsonValue,
                  },
                ]}
              >
                <Input.TextArea rows={8} readOnly={isLojinhaCreation} />
              </Form.Item>
            </div>

            <div className={classes.ActionRow}>
              <Button
                htmlType="button"
                className={`${classes.ActionButton} ${classes.CancelButton}`}
                onClick={handleCancel}
              >
                Cancelar
              </Button>
              <Button
                htmlType="submit"
                type="primary"
                loading={isSaving}
                className={`${classes.ActionButton} ${classes.FinishButton}`}
              >
                Finalizar
              </Button>
            </div>
          </Form>
        )}
      </div>
    </ConfigProvider>
  );
};

export default EditEvent;
